<script>
  import { onDestroy, tick } from "svelte";

  let isOpen = false;
  let chatContainer;
  let chatInstance = null;
  let mounted = false;
  let isLoading = false;
  let loadError = "";

  const env = typeof process !== "undefined" ? process.env || {} : {};

  const normalizeMcpEndpoint = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (raw.startsWith("/mcp/")) return raw;
    if (raw.startsWith("mcp/")) return "/" + raw;
    return `/mcp/${raw}`;
  };

  const mcpEndpoint = normalizeMcpEndpoint(env.SVELTE_APP_MCP_SERVER || "aladv-mcp");

  const destroyChat = () => {
    if (chatInstance && typeof chatInstance.$destroy === "function") {
      chatInstance.$destroy();
    }
    chatInstance = null;
    mounted = false;
  };

  const loadChat = async () => {
    if (mounted || isLoading) return;

    isLoading = true;
    loadError = "";

    try {
      const mod = await import("AdminApp/AI-Chat");
      const AIChat = mod?.default;
      if (!AIChat) throw new Error("AI chat module has no default export.");

      await tick();
      if (!chatContainer) throw new Error("AI chat mount target is not available.");

      chatContainer.innerHTML = "";
      chatInstance = new AIChat({
        target: chatContainer,
        props: {
          mcpEndpoint,
          override: !!mcpEndpoint
        }
      });
      mounted = true;
    } catch (error) {
      loadError = error?.message || "Could not load AI Chat.";
      destroyChat();
    } finally {
      isLoading = false;
    }
  };

  const toggle = async () => {
    if (isOpen) {
      isOpen = false;
      return;
    }

    isOpen = true;
    await loadChat();
  };

  const closeChat = () => {
    isOpen = false;
  };

  onDestroy(destroyChat);
</script>

<button
  type="button"
  class="fixed bottom-4 right-4 z-50 w-14 h-14 rounded-full shadow-lg text-white flex items-center justify-center border-0 cursor-pointer"
  style="background:#34344a"
  on:click={toggle}
  aria-label={isOpen ? "Close AI Chat" : "Open AI Chat"}
>
  {#if isOpen}
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  {:else}
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  {/if}
</button>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="fixed inset-0 z-30 bg-black/30 sm:hidden"
  on:click={closeChat}
  aria-hidden="true"
  style:display={isOpen ? "block" : "none"}
></div>

<div
  class="ai-chat-panel fixed bottom-20 right-4 z-40 rounded-2xl overflow-hidden shadow-xl bg-white"
  style={`display:${isOpen ? "grid" : "none"};width:760px;height:760px;max-height:calc(100dvh - 6rem);max-width:calc(100vw - 2rem);grid-template-rows:1fr`}
>
  {#if loadError}
    <div class="flex flex-col items-center justify-center h-full gap-3 p-6 text-center text-sm" style="color:#6b7280">
      <div>Could not load AI Chat.</div>
      <small>{loadError}</small>
      <button type="button" class="px-4 py-2 rounded-lg border-0 cursor-pointer" on:click={loadChat}>Try again</button>
    </div>
  {:else if isLoading}
    <div class="flex items-center justify-center h-full text-sm" style="color:#6b7280">Loading...</div>
  {/if}
  <div id="ai-chat-mount" bind:this={chatContainer} class="h-full w-full"></div>
</div>

<style>
  #ai-chat-mount {
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  :global(#ai-chat-mount > *) {
    width: 100% !important;
    height: 100% !important;
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;
    scrollbar-width: none;
  }

  :global(#ai-chat-mount > *::-webkit-scrollbar) {
    display: none;
  }

  :global(#ai-chat-mount *) {
    box-sizing: border-box;
    max-width: 100%;
  }

  :global(#ai-chat-mount input),
  :global(#ai-chat-mount select) {
    min-width: 0;
  }

  @media (max-width: 760px) {
    .ai-chat-panel {
      left: 1rem;
      right: 1rem;
      bottom: 5rem;
      width: auto !important;
      height: min(760px, calc(100dvh - 6rem)) !important;
      max-width: none !important;
    }

    :global(#ai-chat-mount div:has(> .ai-chat-input)) {
      display: grid !important;
      grid-template-columns: auto 1fr auto auto !important;
      gap: 0.75rem !important;
      align-items: center !important;
      padding: 0.75rem !important;
      overflow-x: hidden !important;
    }

    :global(#ai-chat-mount .ai-chat-input) {
      grid-column: 1 / -1 !important;
      width: 100% !important;
      min-width: 0 !important;
    }

    :global(#ai-chat-mount div:has(> .ai-chat-input) select) {
      width: 100% !important;
      min-width: 0 !important;
    }
  }

</style>
